import { create } from 'zustand'
import { bulkUpsertSettings, fetchSettings, putSetting, deleteSetting as apiDeleteSetting } from '../api/settings'
import { SYNCED_SETTINGS_KEYS } from '../lib/settingsKeys'

/**
 * In-memory snapshot of every per-user app_setting. Values are kept as the
 * raw JSON-encoded strings returned from the server; useSetting() handles
 * decoding so a malformed blob can't crash hooks that share state via the
 * legacy `local-storage:KEY` custom event.
 *
 * Hydration runs once per session after auth: GET /api/settings populates
 * the store, then any localStorage keys in SYNCED_SETTINGS_KEYS that the
 * server doesn't have yet are uploaded as a one-shot migration and cleared
 * from localStorage. The app waits for `hydrated` before rendering the
 * connected shell so call sites can read synchronously.
 */
interface SettingsState {
  hydrated: boolean
  values: Record<string, string>
  set: (key: string, value: string) => void
  remove: (key: string) => void
  hydrate: () => Promise<void>
  reset: () => void
}

export const useSettingsStore = create<SettingsState>((stateSet, stateGet) => ({
  hydrated: false,
  values: {},
  set: (key, value) => {
    stateSet((s) => ({ values: { ...s.values, [key]: value } }))
    // Fire-and-forget write-through. The optimistic store update already
    // reflects the user's intent — surfacing a server error here would
    // require a UI affordance we don't have today.
    putSetting(key, value).catch((err) => {
      console.warn('[settings] write failed', key, err)
    })
  },
  remove: (key) => {
    stateSet((s) => {
      if (!(key in s.values)) return s
      const next = { ...s.values }
      delete next[key]
      return { values: next }
    })
    apiDeleteSetting(key).catch((err) => {
      console.warn('[settings] delete failed', key, err)
    })
  },
  hydrate: async () => {
    if (stateGet().hydrated) return
    const values = await fetchSettings()
    // One-shot migration: if the server doesn't know about a key but the
    // browser does, push it up so this user keeps their preferences after
    // we cut over to DB-backed settings.
    const pending: Record<string, string> = {}
    for (const key of SYNCED_SETTINGS_KEYS) {
      if (key in values) continue
      try {
        const raw = localStorage.getItem(key)
        if (raw == null) continue
        pending[key] = raw
        values[key] = raw
      } catch {
        // localStorage may be unavailable; ignore.
      }
    }
    if (Object.keys(pending).length > 0) {
      try {
        await bulkUpsertSettings(pending)
      } catch (err) {
        console.warn('[settings] migration upload failed', err)
      }
    }
    // Clear migrated keys from localStorage so a future read can't shadow
    // a fresher DB value. Leave non-synced keys (auth, caches) alone.
    for (const key of SYNCED_SETTINGS_KEYS) {
      try {
        localStorage.removeItem(key)
      } catch {
        // ignore
      }
    }
    stateSet({ values, hydrated: true })
  },
  reset: () => stateSet({ hydrated: false, values: {} }),
}))
